import { useTranslation } from "react-i18next";
import { SEO } from "@/components/seo/SEO";

interface PrivacyProps {
  inline?: boolean;
}

export const Privacy = ({ inline = false }: PrivacyProps) => {
  const { t } = useTranslation();

  return (
    <>
      {!inline && <SEO />}
      <div className="bg-neutral-50 min-h-screen text-neutral-900">
        {/* Removed duplicated <SEO /> here to avoid multiple head tags when embedded */}
        <SEO 
          title={t('privacy.title', 'Privacy Policy & Data Retention')}
          description={t('privacy.description', 'Learn about how INS Organization collects, uses, and protects your personal data.')}
        />
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8">{t('privacy.title', 'Privacy Policy & Data Retention')}</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-neutral-600 mb-8">
                {t('privacy.intro', 'This privacy policy explains how INS Organization collects, uses, and protects your personal information when you use our humanitarian aid platform.')}
              </p>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataCollection.title', 'Data We Collect')}</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium mb-2">{t('privacy.dataCollection.personal', 'Personal Information')}</h3>
                    <ul className="list-disc list-inside space-y-1 text-neutral-700">
                      <li>{t('privacy.dataCollection.personal.email', 'Email addresses for account creation and communication')}</li>
                      <li>{t('privacy.dataCollection.personal.name', 'Names and contact information for project coordination')}</li>
                      <li>{t('privacy.dataCollection.personal.phone', 'Phone numbers for emergency contact and verification')}</li>
                      <li>{t('privacy.dataCollection.personal.organization', 'Organization details for partner verification')}</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-2">{t('privacy.dataCollection.technical', 'Technical Information')}</h3>
                    <ul className="list-disc list-inside space-y-1 text-neutral-700">
                      <li>{t('privacy.dataCollection.technical.ip', 'IP addresses and device information for security')}</li>
                      <li>{t('privacy.dataCollection.technical.cookies', 'Cookies and similar technologies for functionality')}</li>
                      <li>{t('privacy.dataCollection.technical.usage', 'Usage analytics to improve our services')}</li>
                      <li>{t('privacy.dataCollection.technical.location', 'GPS coordinates for project reporting (with consent)')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataUsage.title', 'How We Use Your Data')}</h2>
                <ul className="list-disc list-inside space-y-2 text-neutral-700">
                  <li>{t('privacy.dataUsage.coordination', 'Coordinate humanitarian aid projects and partnerships')}</li>
                  <li>{t('privacy.dataUsage.communication', 'Communicate about project updates and important information')}</li>
                  <li>{t('privacy.dataUsage.verification', 'Verify partner and agent credentials for security')}</li>
                  <li>{t('privacy.dataUsage.analytics', 'Analyze platform usage to improve our services')}</li>
                  <li>{t('privacy.dataUsage.compliance', 'Comply with legal and regulatory requirements')}</li>
                </ul>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataRetention.title', 'Data Retention')}</h2>
                <div className="space-y-4">
                  <p className="text-neutral-700">
                    {t('privacy.dataRetention.general', 'We retain your personal data only as long as necessary for the purposes outlined in this policy.')}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-medium text-blue-900 mb-2">{t('privacy.dataRetention.timeline', 'Retention Timeline')}</h3>
                    <ul className="space-y-1 text-blue-800">
                      <li>• {t('privacy.dataRetention.timeline.active', 'Active accounts: Until account closure + 2 years')}</li>
                      <li>• {t('privacy.dataRetention.timeline.projects', 'Project data: 7 years for audit purposes')}</li>
                      <li>• {t('privacy.dataRetention.timeline.analytics', 'Analytics data: 2 years maximum')}</li>
                      <li>• {t('privacy.dataRetention.timeline.legal', 'Legal compliance: As required by law')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataRights.title', 'Your Rights (GDPR/KVKK)')}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">{t('privacy.dataRights.access', 'Access & Portability')}</h3>
                    <ul className="space-y-1 text-neutral-700 text-sm">
                      <li>• {t('privacy.dataRights.access.details', 'Request a copy of your personal data')}</li>
                      <li>• {t('privacy.dataRights.access.export', 'Export your data in a portable format')}</li>
                      <li>• {t('privacy.dataRights.access.info', 'Get information about how we use your data')}</li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">{t('privacy.dataRights.control', 'Control & Deletion')}</h3>
                    <ul className="space-y-1 text-neutral-700 text-sm">
                      <li>• {t('privacy.dataRights.control.update', 'Update or correct your information')}</li>
                      <li>• {t('privacy.dataRights.control.delete', 'Request deletion of your personal data')}</li>
                      <li>• {t('privacy.dataRights.control.restrict', 'Restrict processing of your data')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.dataSecurity.title', 'Data Security')}</h2>
                <div className="space-y-4">
                  <p className="text-neutral-700">
                    {t('privacy.dataSecurity.description', 'We implement industry-standard security measures to protect your personal data:')}
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-neutral-700">
                    <li>{t('privacy.dataSecurity.encryption', 'End-to-end encryption for sensitive data transmission')}</li>
                    <li>{t('privacy.dataSecurity.access', 'Role-based access controls and authentication')}</li>
                    <li>{t('privacy.dataSecurity.monitoring', 'Regular security monitoring and updates')}</li>
                    <li>{t('privacy.dataSecurity.training', 'Staff training on data protection best practices')}</li>
                  </ul>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.cookies.title', 'Cookies & Analytics')}</h2>
                <div className="space-y-4">
                  <p className="text-neutral-700">
                    {t('privacy.cookies.description', 'We use cookies and similar technologies to enhance your experience:')}
                  </p>
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-medium">{t('privacy.cookies.essential', 'Essential Cookies')}</h3>
                      <p className="text-sm text-neutral-600">{t('privacy.cookies.essential.desc', 'Required for basic platform functionality and security.')}</p>
                    </div>
                    <div>
                      <h3 className="font-medium">{t('privacy.cookies.analytics', 'Analytics Cookies')}</h3>
                      <p className="text-sm text-neutral-600">{t('privacy.cookies.analytics.desc', 'Optional cookies to help us improve our services. You can opt-out at any time.')}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.contact.title', 'Contact & Requests')}</h2>
                <div className="bg-neutral-100 rounded-lg p-6">
                  <p className="text-neutral-700 mb-4">
                    {t('privacy.contact.description', 'To exercise your data rights or ask questions about this policy:')}
                  </p>
                  <div className="space-y-2">
                    <p><strong>{t('privacy.contact.email', 'Email:')}</strong> info@insorganizasyon.org</p>
                    <p><strong>{t('privacy.contact.response', 'Response Time:')}</strong> {t('privacy.contact.responseTime', 'Within 30 days')}</p>
                    <p><strong>{t('privacy.contact.verification', 'Verification:')}</strong> {t('privacy.contact.verificationDesc', 'We may need to verify your identity before processing requests')}</p>
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{t('privacy.updates.title', 'Policy Updates')}</h2>
                <p className="text-neutral-700">
                  {t('privacy.updates.description', 'We may update this privacy policy from time to time. We will notify you of any significant changes via email or platform notification. The last updated date is shown below.')}
                </p>
                <p className="text-sm text-neutral-500 mt-4">
                  {t('privacy.updates.lastUpdated', 'Last updated: January 15, 2024')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
